import { HttpParams } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { FlashMessagesService } from 'angular2-flash-messages'
import { forkJoin } from 'rxjs'

import { environment } from '../../../../environments/environment'
import { appConstants } from '../../../app.constants'
import { DynamicField } from '../../../common/interfaces/dynamic-field.interface'
import { Field } from '../../../common/interfaces/field.interface'
import { Granulometry } from '../../../common/interfaces/granulometry.interface'
import { Mission } from '../../../common/interfaces/mission.interface'
import { Sample } from '../../../common/interfaces/sample.interface'
import { SearchResult } from '../../../common/interfaces/search-result.interface'
import { Subcategory } from '../../../common/interfaces/subcategory.interface'
import { Tare } from '../../../common/interfaces/tare.interface'
import { BreadcrumbService } from '../../../common/services/breadcrumb.service'
import { ResourceService } from '../../../common/services/resource.service'
import { Company } from '../../../common/interfaces/company.interface'
import { Role } from '../../../common/enums/role.enum'
import { AuthService } from 'src/app/common/services/auth.service'
import { User } from '../../../common/interfaces/user.interface'
import { Category } from '../../../common/interfaces/category.interface'

function minLengthArray(min: number): ValidatorFn {
  return (control: AbstractControl) => {
    const array = control as FormArray
    return array.length >= min
      ? null
      : { minLengthArray: { requiredLength: min } }
  }
}

@Component({
  selector: 'app-mission-create-edit',
  templateUrl: './mission-create-edit.component.html',
  styleUrls: ['./mission-create-edit.component.scss']
})
export class MissionCreateEditComponent implements OnInit {
  mode: string
  mission: Mission
  companies: Company[]
  loading: boolean
  submitLoading: boolean
  role: Role;
  me: User;

  dynamicFields: DynamicField[] = []
  initialCompaniesSearchResults: { companyIds: string[] }

  storagePath: string = environment.storagePath
  requiredValidator: ValidatorFn[] = [Validators.required]

  form: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    customer: ['', Validators.required],
    minPhotos: [
      3,
      [Validators.required, Validators.min(0), Validators.max(8)]
    ],
    categories: this.formBuilder.array([], minLengthArray(1)),
    tares: [],
    granulometries: [],
    fields: this.formBuilder.array([]),
    companiesIds: []
  })

  constructor(
    private resourceService: ResourceService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private flashMessagesService: FlashMessagesService,
    private breadcrumbService: BreadcrumbService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.role = this.authService.getRole();
    this.mode = this.activatedRoute.snapshot.data.mode
    this.authService.getFullMe().then(user => {
      this.me = typeof(user) ? user as User : null;
      if (this.role !== 'Admin') {
        this.initialCompaniesSearchResults = {
          companyIds: [this.me.company.id.toString()]
        }
        this.form.get('companiesIds').setValue([this.me.company.id.toString()]);
      }
      this.mode = this.activatedRoute.snapshot.data.mode
      
    // Get Companies
    let companyListParams = new HttpParams()
    companyListParams = companyListParams.set('withoutPagination', 'true')
    this.resourceService
      .list('companies', companyListParams)
      .subscribe((companyRes) => {
        this.companies = this.role === 'Admin' ? companyRes : companyRes.filter(c => c.id === this.me.company.id);
      })

    if (this.mode === 'create') {
      this.breadcrumbService.breadcrumbLinks.next([
        {
          path: '/missions',
          label: 'Missions'
        },
        {
          label: 'Nouvelle mission'
        }
      ])

      // Add default dynamic fields
      const formArray: FormArray = this.form.get('fields') as FormArray
      appConstants.DEFAULT_DYNAMIC_FIELDS.forEach((field: DynamicField) => {
        const fieldGroup = this.formBuilder.group(field);
        if (!fieldGroup.contains('options')) {
          fieldGroup.addControl('options', this.formBuilder.control([]));
        }
        formArray.push(fieldGroup);
      })

      // Start with one empty category (with one empty subcategory)
      this.addCategory()
    } else {
      // Modes 'edit' et 'duplicate' : on charge la mission source. En duplication,
      // on dépouille les IDs (catégories, sous-catégories, champs) et les verrous
      // editOnly pour repartir sur des entités neuves, entièrement éditables.
      const isDuplicate = this.mode === 'duplicate'
      this.loading = true
      let sampleParams: HttpParams = new HttpParams()
      sampleParams = sampleParams.set(
        'missionsIds',
        this.activatedRoute.snapshot.params.id
      )
      sampleParams = sampleParams.set('withoutPagination', 'true')

      forkJoin([
        this.resourceService.show(
          'missions',
          this.activatedRoute.snapshot.params.id
        ),
        this.resourceService.list('samples', sampleParams)
      ]).subscribe(
        ([missionRes, sampleRes]: [Mission, Sample[]]) => {
          this.loading = false
          this.mission = missionRes
          this.mission.samples = sampleRes

          this.form.controls.name.setValue(
            isDuplicate ? `${missionRes.name} (copie)` : missionRes.name
          )
          this.form.controls.customer.setValue(missionRes.customer)
          this.form.controls.minPhotos.setValue(
            missionRes.minPhotos != null ? missionRes.minPhotos : 3
          )
          this.form.controls.tares.setValue(
            missionRes.tares.map((t: Tare) => ({
              display: t.weight,
              value: t.weight
            }))
          )
          this.form.controls.granulometries.setValue(
            missionRes.granulometries.map((g: Granulometry) => ({
              display: g.name,
              value: g.name,
              // En duplication, toutes les granulométries sont recréées : aucune
              // n'est verrouillée.
              editOnly: isDuplicate ? false : !!g.weighingCount
            }))
          )
          // Group the mission's subcategories by category (API returns them
          // sorted by rank, so insertion order is already correct). En duplication,
          // on retire les IDs et les compteurs de pesées (nouvelles entités).
          const categoryGroups: Map<number, FormGroup> = new Map()
          ;(missionRes.subcategories || []).forEach((sC: Subcategory) => {
            let categoryGroup = categoryGroups.get(sC.category.id)
            if (!categoryGroup) {
              categoryGroup = this.createCategoryGroup(
                isDuplicate ? { ...sC.category, id: null } : sC.category
              )
              categoryGroups.set(sC.category.id, categoryGroup)
              this.categoriesArray.push(categoryGroup)
            }
            ;(categoryGroup.get('subcategories') as FormArray).push(
              this.createSubcategoryGroup(
                isDuplicate ? { ...sC, id: null, weighingCount: 0 } : sC
              )
            )
          })

          const companyIdStrings: string[] = missionRes.companies.map(
            (c: Company) => c.id.toString()
          )
          this.initialCompaniesSearchResults = {
            companyIds: companyIdStrings
          }

          this.form.controls.companiesIds.setValue(companyIdStrings)

          const formArray: FormArray = this.form.get('fields') as FormArray
          missionRes.fields.forEach((field: Field) => {
            const oo = field.options
              ? field.options.map((o) => {
                  // En duplication les options doivent rester éditables (readonly
                  // false), en édition elles sont figées (déjà persistées).
                  return { display: o.value, value: o.value, readonly: !isDuplicate }
                })
              : []
            const fieldObject: DynamicField = {
              id: isDuplicate ? undefined : field.id,
              name: field.name,
              required: field.required,
              editOnly: isDuplicate ? false : !!field.answerCount,
              type: field.type as any,
              options: oo,
              position: field.position
            }
            const fieldGroupConfig: any = {
              ...fieldObject,
              options: this.formBuilder.control(oo)
            }
            // isNew : rend le champ dupliqué modifiable/supprimable (type, options,
            // obligatoire) comme un champ ajouté manuellement.
            if (isDuplicate) {
              fieldGroupConfig.isNew = true
            }
            formArray.push(this.formBuilder.group(fieldGroupConfig))
            // We store it in array to adapt UI based on editOnly prop
            this.dynamicFields.push(fieldObject)
          })

          if (isDuplicate) {
            this.breadcrumbService.breadcrumbLinks.next([
              {
                path: '/missions',
                label: 'Missions'
              },
              {
                label: 'Dupliquer la mission'
              }
            ])
          } else {
            this.breadcrumbService.breadcrumbLinks.next([
              {
                path: '/missions',
                label: 'Missions'
              },
              {
                path: '/missions/' + this.mission.id,
                label: this.mission.name
              },
              {
                label: 'Editer'
              }
            ])
          }
        },
        (error) => {
          this.loading = false
          this.flashMessagesService.show(
            'Une erreur a eu lieu : Impossible de récupérer le(s) élémént(s) désiré(s).',
            {
              cssClass: 'notification is-danger',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            }
          )
        }
      )
    }
    
    });
  }

  submit(form: FormGroup) {
    this.submitLoading = true

    const formData = new FormData()
    formData.append('name', form.value.name)
    formData.append('customer', form.value.customer)
    formData.append('minPhotos', form.value.minPhotos)

    if (form.value.tares) {
      form.value.tares.forEach((tare: { display: string; value: string }) => {
        formData.append(
          this.isCreateMode ? 'taresToCreate' : 'taresToSync',
          tare.value
        )
      })
    }
    if (form.value.granulometries) {
      form.value.granulometries.forEach(
        (granulometry: {
          display: string
          value: string
          readonly: boolean
        }) => {
          // We don't send "readonly" Granulometries (with Weighings) because they will not be updated
          if (!granulometry.readonly) {
            formData.append(
              this.isCreateMode
                ? 'granulometriesToCreate'
                : 'granulometriesToSync',
              granulometry.value
            )
          }
        }
      )
    }

    if (form.value.fields) {
      const fields: DynamicField[] = form.value.fields.filter(
        (f: DynamicField) => f.name
      )
      fields.forEach((field: DynamicField) => {
        if (field.type === 0)
            field.required = false;
        formData.append(
          this.isCreateMode ? 'fieldsToCreate' : 'fieldsToSync',
          JSON.stringify(field)
        )
      })
    }

    // Categories + nested subcategories: one JSON entry per category
    // (same FormData pattern as dynamic fields)
    this.categoriesArray.controls.forEach((categoryGroup: FormGroup) => {
      const categoryValue = categoryGroup.value
      const categoryPayload = {
        id: categoryValue.id || undefined,
        name: categoryValue.name,
        position: Number(categoryValue.position) || 1,
        subcategories: (categoryValue.subcategories || []).map((sub: any) => ({
          id: sub.id || undefined,
          name: sub.name,
          position: Number(sub.position) || 1,
          photoMode: sub.photoRequired ? 'required' : 'optional',
          quantityMode: sub.quantityMode,
          volumeMode: sub.volumeMode
        }))
      }
      formData.append(
        this.isCreateMode ? 'categoriesToCreate' : 'categoriesToSync',
        JSON.stringify(categoryPayload)
      )
    })

    form.value.companiesIds.forEach((companyId: number) => {
      formData.append('companyIds', companyId.toString())
    })

    if (this.isCreateMode) {
      this.resourceService.store('missions', formData).subscribe(
        (createdMission: Mission) => {
          this.submitLoading = false
          this.flashMessagesService.show(`La mission a bien été créé`, {
            cssClass: 'notification is-success',
            timeout: appConstants.FLASH_MESSAGE_TIMEOUT
          })
          this.router.navigate(['/missions', createdMission.id])
        },
        (err) => {
          this.submitLoading = false
          this.flashMessagesService.show(
            'Error ' +
              JSON.stringify(err.error.message.map((m) => m.constraints)),
            {
              cssClass: 'notification is-danger',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            }
          )
        }
      )
    } else {
      // Update existing resource
      this.resourceService
        .update('missions', this.mission.id, formData)
        .subscribe(
          (res) => {
            this.submitLoading = false
            this.flashMessagesService.show(`La mission a bien été mis à jour`, {
              cssClass: 'notification is-success',
              timeout: appConstants.FLASH_MESSAGE_TIMEOUT
            })
            this.router.navigate(['/missions', this.mission.id])
          },
          (err) => {
            this.submitLoading = false
            this.flashMessagesService.show(
              'Error ' +
                JSON.stringify(err.error.message.map((m) => m.constraints)),
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          }
        )
    }
  }

  // 'create' et 'duplicate' partagent le même flux d'enregistrement (POST +
  // payloads *ToCreate) : la duplication recrée toujours des entités neuves.
  get isCreateMode(): boolean {
    return this.mode === 'create' || this.mode === 'duplicate'
  }

  get categoriesArray(): FormArray {
    return this.form.get('categories') as FormArray
  }

  subcategoriesArray(categoryIndex: number): FormArray {
    return this.categoriesArray.at(categoryIndex).get('subcategories') as FormArray
  }

  addCategory() {
    const categoryGroup: FormGroup = this.createCategoryGroup()
    categoryGroup
      .get('position')
      .setValue(this.findMaxPositionInArray(this.categoriesArray) + 1)
    ;(categoryGroup.get('subcategories') as FormArray).push(
      this.createSubcategoryGroup()
    )
    this.categoriesArray.push(categoryGroup)
  }

  removeCategory(categoryIndex: number) {
    if (this.isCategoryRemovable(categoryIndex)) {
      this.categoriesArray.removeAt(categoryIndex)
    }
  }

  // A category can only be removed if none of its subcategories has weighings
  isCategoryRemovable(categoryIndex: number): boolean {
    return this.subcategoriesArray(categoryIndex).controls.every(
      (subcategoryGroup) => !subcategoryGroup.get('editOnly').value
    )
  }

  addSubcategory(categoryIndex: number) {
    const subcategories: FormArray = this.subcategoriesArray(categoryIndex)
    const subcategoryGroup: FormGroup = this.createSubcategoryGroup()
    subcategoryGroup
      .get('position')
      .setValue(this.findMaxPositionInArray(subcategories) + 1)
    subcategories.push(subcategoryGroup)
  }

  removeSubcategory(categoryIndex: number, subcategoryIndex: number) {
    const subcategories: FormArray = this.subcategoriesArray(categoryIndex)
    if (!subcategories.at(subcategoryIndex).get('editOnly').value) {
      subcategories.removeAt(subcategoryIndex)
    }
  }

  private createCategoryGroup(category?: Category): FormGroup {
    return this.formBuilder.group({
      id: [category ? category.id : null],
      name: [category ? category.name : '', Validators.required],
      position: [
        category && category.position ? category.position : 1,
        Validators.required
      ],
      subcategories: this.formBuilder.array([], minLengthArray(1))
    })
  }

  private createSubcategoryGroup(subcategory?: Subcategory): FormGroup {
    return this.formBuilder.group({
      id: [subcategory ? subcategory.id : null],
      name: [subcategory ? subcategory.name : '', Validators.required],
      position: [
        subcategory && subcategory.position ? subcategory.position : 1,
        Validators.required
      ],
      photoRequired: [subcategory ? subcategory.photoMode !== 'optional' : true],
      quantityMode: [
        subcategory ? subcategory.quantityMode || 'optional' : 'optional'
      ],
      volumeMode: [subcategory ? subcategory.volumeMode || 'hidden' : 'hidden'],
      editOnly: [subcategory ? !!subcategory.weighingCount : false]
    })
  }

  private findMaxPositionInArray(formArray: FormArray): number {
    let max = 0
    formArray.controls.forEach((control) => {
      const position = Number(control.get('position').value)
      if (position > max) {
        max = position
      }
    })
    return max
  }

  addDynamicField() {
    const formArray: FormArray = this.form.get('fields') as FormArray
    const max = this.findMaxPosition(formArray.value);
    formArray.push(
      this.formBuilder.group({ name: '', required: false, options: [], type: 0, isNew: true, position: max + 1})
    )
  }

  findMaxPosition(fields: Array<Field>) {
    let result = 0;
    fields.forEach(f => {
      if(f.position > result)
        result = f.position});
    return result;
  }

  // An existing (persisted) dynamic field can only be deleted while the mission
  // has no sample (prélèvement). Newly added fields can always be removed.
  get canDeleteExistingFields(): boolean {
    return (
      this.mode === 'edit' &&
      !!this.mission &&
      !this.mission.sampleCount
    )
  }

  removeDynamicField(index: number) {
    const formArray: FormArray = this.form.get('fields') as FormArray
    if (formArray.get([index]).get('isNew') || this.canDeleteExistingFields)
      formArray.removeAt(index)
  }

  onSelectedCompaniesSearchResultsChanged(searchResults: SearchResult[]) {
    this.form
      .get('companiesIds')
      .setValue(searchResults.map((s: SearchResult) => s.id.toString()))
  }
}
